/**
 * 分拣批次页面 mock 状态 reducer（纯函数）。写操作的计算在 mock/batchMockActions.ts 的 plan 函数中完成，
 * 这里只负责把计划应用到状态上。
 *
 * todo batch mock: 后端接入后各 action 改为调用对应 Mutation 并 refetch，删除本 reducer 的数据变更逻辑。
 */
import { CreateBatchesPlan, MovePlan } from '../mock/batchMockActions';
import { listBatchBags } from '../mock/batchMockDerive';
import { movedOutKey, SortingBatchMockState } from '../mock/mockState';
import { BatchBag } from '../types';
import { canEndSorting, hasBagsOrParcels } from '../utils/batchRules';

export type SortingBatchAction =
    | { type: 'RESET'; state: SortingBatchMockState }
    | { type: 'REFRESH'; time: number }
    | { type: 'CREATE_BATCHES'; plan: CreateBatchesPlan }
    | { type: 'CANCEL_BATCHES'; ids: number[] }
    | { type: 'END_SORTING'; ids: number[] }
    | { type: 'ADD_BAGS'; bagsByBatchId: Record<number, BatchBag[]> }
    | { type: 'APPLY_MOVE'; plan: MovePlan };

const mergeLists = <T>(base: Record<string, T[]>, add: Record<string, T[]>): Record<string, T[]> => {
    const out = { ...base };
    Object.keys(add).forEach((k) => {
        out[k] = [...(out[k] || []), ...add[k]];
    });
    return out;
};

export const sortingBatchReducer = (state: SortingBatchMockState, action: SortingBatchAction): SortingBatchMockState => {
    switch (action.type) {
        case 'RESET':
            return action.state;
        case 'REFRESH':
            return { ...state, refreshedAt: action.time };
        case 'CREATE_BATCHES': {
            const { batches, endIds, logs } = action.plan;
            // 兜底校验：批次名与本仓已有发出批次（含已取消）重名时整体拒绝（批次名唯一，plan §10-Q11）
            const existing = new Set(state.outbound.map((b) => b.batchNumber));
            if (!batches.length || batches.some((b) => existing.has(b.batchNumber))) return state;
            const rest = state.outbound.map((b) => (endIds.includes(b.id) ? { ...b, status: 'SORTING_ENDED' as const, endedBySync: true } : b));
            return {
                ...state,
                outbound: [...batches, ...rest],
                nextBatchId: state.nextBatchId + batches.length,
                sessionLogs: [...state.sessionLogs, ...logs],
            };
        }
        case 'CANCEL_BATCHES': {
            // 兜底校验：含包牌或包裹的批次不可取消
            const targets = state.outbound.filter((b) => action.ids.includes(b.id));
            if (!targets.length || targets.some((b) => hasBagsOrParcels(b, listBatchBags(state, b).length))) return state;
            return { ...state, outbound: state.outbound.map((b) => (action.ids.includes(b.id) ? { ...b, status: 'CANCELLED' } : b)) };
        }
        case 'END_SORTING':
            return {
                ...state,
                outbound: state.outbound.map((b) => (action.ids.includes(b.id) && canEndSorting(b.status) ? { ...b, status: 'SORTING_ENDED' } : b)),
            };
        case 'ADD_BAGS': {
            const extraBags = { ...state.extraBags };
            const outbound = state.outbound.map((b) => {
                const bags = action.bagsByBatchId[b.id];
                if (!bags || !bags.length) return b;
                extraBags[b.id] = [...(extraBags[b.id] || []), ...bags];
                return { ...b, createdBags: b.createdBags + bags.length };
            });
            return { ...state, outbound, extraBags };
        }
        case 'APPLY_MOVE': {
            const { plan } = action;
            const movedOutBags = { ...state.movedOutBags };
            plan.movedOutKeys.forEach((k) => { movedOutBags[k] = true; });
            const removedNumbers = { ...state.removedNumbers };
            plan.removedNumbers.forEach((n) => { removedNumbers[n] = true; });
            // 整包移入的包牌重新属于目标批次：清除它此前离开该批次、被移出批次的标记
            const movedInBags = { ...state.movedInBags };
            state.outbound.forEach((target) => {
                const bags = plan.movedInBags[target.id];
                if (!bags || !bags.length) return;
                bags.forEach((g) => {
                    delete movedOutBags[movedOutKey(target.batchNumber, g.bagNumber)];
                    delete removedNumbers[g.bagNumber];
                });
                movedInBags[target.id] = [...(movedInBags[target.id] || []), ...bags];
            });
            return {
                ...state,
                sessionLogs: [...state.sessionLogs, ...plan.logs],
                bagMoves: mergeLists(state.bagMoves, plan.bagMoves),
                movedInBags,
                parcelMoveIns: mergeLists(state.parcelMoveIns, plan.parcelMoveIns),
                movedOutBags,
                removedNumbers,
            };
        }
        default:
            return state;
    }
};
